import { SharedAngular } from '@Client/@types/sharedAngular';
import { FlowsToDoBoxFilters } from '@Client/runner.flows.todo/FlowsTodo';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import {
  IActorDeletedMessage,
  IFlowModelNameChangedMessage,
  IRunnerNewFlowCommentCountMessage,
  IWithdrawFlowMessage
} from '@Shared.Angular/@types/pubSub';
import angular from 'angular';
import { IGroupedFlowsForDisplay } from './flows.formatter';

export default class FlowTodoService {
  public static $inject = [
    'flowApiService',
    '$q',
    'pubsubService',
    'runnerFlowsFormatter'
  ];

  public selectedCategoryId: Guid = EmptyGuid;
  public selectedTagIds: Guid[] = [];
  public selectedFlowModelId: Guid = EmptyGuid;
  public selectedBoxFilterId: FlowsToDoBoxFilters = FlowsToDoBoxFilters.ALL;

  private groupedFlows: IGroupedFlowsForDisplay[] = [];
  private groupedFlowsPromise: angular.IPromise<IGroupedFlowsForDisplay[]>;

  constructor(
    private flowApiService: FlowApiService,
    private $q: angular.IQService,
    private pubsubService: SharedAngular.PubSubService,
    private runnerFlowsFormatter: RunnerFlowsFormatterService
  ) {
    this.groupedFlowsPromise = this.$q.resolve(this.groupedFlows);

    const subscriberId = 'flowTodoService';
    this.pubsubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      (event: string, eventData: string) => {
        const { id, name } = JSON.parse(
          eventData
        ) as IFlowModelNameChangedMessage;
        this.groupedFlows.forEach((group) => {
          if (group.flowModelId === id) {
            group.groupName = name;
          }
        });
      },
      subscriberId
    );
    this.pubsubService.subscribeMany(
      [
        'SIGNALR_RUNNER_START_FLOW',
        'SIGNALR_RUNNER_COMPLETE_STEP',
        'SIGNALR_RUNNER_REASSIGN_STEP',
        'SIGNALR_RUNNER_USER_TEAM_UPDATED',
        'SIGNALR_RUNNER_STEP_TASK_CANCELLED',
        'SIGNALR_RUNNER_STEP_TASK_CREATED',
        'SIGNALR_RUNNER_STEP_TASK_UPDATED',
        'SIGNALR_RUNNER_STEP_INTEGRATION_PROCESSING_UPDATED'
      ],
      () => this.getRefreshedFlows(),
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_RUNNER_WITHDRAW_FLOW',
      (event, eventData: string) => {
        const { flowId } = JSON.parse(eventData) as IWithdrawFlowMessage;
        this.groupedFlows.forEach((group) => {
          group.flows = group.flows.filter((flow) => flow.flowId !== flowId);
        });
        this.groupedFlows = this.groupedFlows.filter(
          (group) => group.flows.length > 0
        );
        this.groupedFlowsPromise = this.$q.resolve(this.groupedFlows);
        this.pubsubService.publish(
          'SIDEMENU_COUNT_UPDATED',
          this.getFlowCount()
        );
      },
      subscriberId
    );

    this.pubsubService.subscribe(
      'SIGNALR_ACTOR_DELETED',
      (event, eventData: string) => {
        const { actorName, replacementActorName } = JSON.parse(
          eventData
        ) as IActorDeletedMessage;
        this.groupedFlows.forEach((group) => {
          group.flows.forEach((flow) => {
            if (!flow.waitingForName.includes(actorName)) {
              return;
            }
            const names = flow.waitingForName.split(',').map((name) => {
              return name.trim() === actorName
                ? replacementActorName
                : name.trim();
            });
            flow.waitingForName = names.join(', ');
          });
        });
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_RUNNER_NEW_FLOW_COMMENT_COUNT',
      (event, eventData: string) => {
        const { flowId, commentCount } = JSON.parse(
          eventData
        ) as IRunnerNewFlowCommentCountMessage;
        this.groupedFlows.forEach((group) => {
          group.flows.forEach((flow) => {
            if (flow.flowId !== flowId) {
              return;
            }
            flow.commentCount = commentCount;
          });
        });
      },
      subscriberId
    );
  }

  /** Get Flows ToDo from the API */
  public getRefreshedFlows() {
    this.groupedFlowsPromise = this.flowApiService
      .getFlowsTodo()
      .then((response) => {
        this.groupedFlows =
          this.runnerFlowsFormatter.setGroupedFlowDisplayProperties(
            response || []
          );
        this.pubsubService.publish(
          'SIDEMENU_COUNT_UPDATED',
          this.getFlowCount()
        );
        return this.groupedFlows;
      });
    return this.groupedFlowsPromise;
  }

  /** Return cached Flows ToDo if any, otherwise get from the API */
  public getFlows() {
    return this.groupedFlowsPromise.then((groupedFlows) => {
      if (groupedFlows.length > 0) {
        return groupedFlows;
      }
      return this.getRefreshedFlows();
    });
  }

  public getFlowCount() {
    return this.groupedFlows.reduce(
      (count, group) => count + group.flows.length,
      0
    );
  }
}

angular
  .module('flowingly.runner.services')
  .service('flowTodoService', FlowTodoService);

export type FlowTodoServiceType = InstanceType<typeof FlowTodoService>;
